/**
 * Extended Image Extension for TipTap
 *
 * Extends the default TipTap Image extension to add template-specific attributes:
 * - blockId - for template style matching
 * - alignment - rendered as CSS class (float left/right/center)
 * - width/height - explicit dimensions from template
 * - aspectRatio - for maintaining proportions
 * - isAIGenerated - flag for AI-generated images
 *
 * IMPORTANT: This extension uses a custom NodeView to handle image alignment correctly.
 * TipTap's built-in ResizableNodeView hardcodes `justify-content: flex-start` which
 * prevents CSS-based centering. Our custom NodeView dynamically sets justify-content
 * based on the alignment attribute.
 */

import Image from '@tiptap/extension-image';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { NodeViewRendererProps } from '@tiptap/core';
import { mergeAttributes } from '@tiptap/core';


/**
 * Maps alignment value to CSS justify-content value
 */
const alignmentToJustifyContent: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

/**
 * Custom NodeView that wraps images in a container with proper alignment.
 * This bypasses TipTap's ResizableNodeView which hardcodes justify-content: flex-start.
 */
function createImageNodeView(
  props: NodeViewRendererProps,
  options: {
    minWidth?: number;
    minHeight?: number;
    directions?: string[];
  }
) {
  const { node, getPos, editor, HTMLAttributes } = props;
  const { minWidth = 50, minHeight = 50 } = options;

  // Create the container div
  const container = document.createElement('div');
  container.dataset.resizeContainer = '';
  container.dataset.node = 'image';

  // Figure groups the image wrapper with its caption. display:table makes the
  // figure shrink-wrap to the image width so the caption wraps beneath it
  // instead of stretching the row.
  const figure = document.createElement('figure');
  figure.dataset.imageFigure = '';
  figure.style.display = 'table';
  figure.style.margin = '0';

  // Create the wrapper div (for resize handles)
  const wrapper = document.createElement('div');
  wrapper.dataset.resizeWrapper = '';
  wrapper.style.position = 'relative';
  wrapper.style.display = 'inline-block';

  // Caption shown beneath the image; mirrors the stored caption attribute.
  // Styled via .tiptap-image-caption (template-content.css).
  const captionEl = document.createElement('figcaption');
  captionEl.dataset.imageCaption = '';
  captionEl.className = 'tiptap-image-caption';
  captionEl.contentEditable = 'false';

  let currentCaption: string | null = null;
  const applyCaption = (caption: unknown) => {
    const text = typeof caption === 'string' ? caption.trim() : '';
    if (text === currentCaption) return;
    currentCaption = text;
    captionEl.textContent = text;
    captionEl.style.display = text ? '' : 'none';
  };
  applyCaption(node.attrs.caption);

  // Create the img element
  const img = document.createElement('img');

  // Apply HTML attributes from extension config
  const attrs = mergeAttributes(HTMLAttributes);
  Object.entries(attrs).forEach(([key, value]) => {
    if (value != null && key !== 'width' && key !== 'height' && key !== 'style') {
      img.setAttribute(key, String(value));
    }
  });

  // Set the src (may be empty when WP upload is still pending/failed)
  img.src = node.attrs.src || '';
  img.alt = node.attrs.alt || '';
  img.title = node.attrs.title || '';

  // Placeholder shown when src is empty or image fails to load
  const placeholder = document.createElement('div');
  placeholder.style.cssText = [
    'display:none',
    'align-items:center',
    'justify-content:center',
    'gap:8px',
    'width:100%',
    'min-height:180px',
    'background:#f9fafb',
    'border:2px dashed #d1d5db',
    'border-radius:8px',
    'color:#9ca3af',
    'font-size:13px',
    'font-family:sans-serif',
    'user-select:none',
  ].join(';');
  placeholder.innerHTML =
    '<svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">' +
    '<rect x="3" y="3" width="18" height="18" rx="2"/>' +
    '<circle cx="8.5" cy="8.5" r="1.5"/>' +
    '<polyline points="21 15 16 10 5 21"/>' +
    '</svg><span>Image generating…</span>';

  const showPlaceholder = () => {
    img.style.display = 'none';
    placeholder.style.display = 'flex';
    container.style.visibility = '';
  };
  const showImage = () => {
    placeholder.style.display = 'none';
    img.style.display = '';
    container.style.visibility = '';
  };

  img.onerror = showPlaceholder;

  // Apply alignment class
  const alignment = node.attrs.alignment || 'center';
  img.classList.add(`align-${alignment}`);

  // Apply block ID if present
  if (node.attrs.blockId) {
    img.setAttribute('data-block-id', node.attrs.blockId);
  }

  // Apply AI generated flag if present
  if (node.attrs.isAIGenerated) {
    img.setAttribute('data-ai-generated', 'true');
  }

  if (node.attrs.isStockPhoto) {
    img.setAttribute('data-stock-photo', 'true');
  }

  // Apply aspect ratio if present
  if (node.attrs.aspectRatio) {
    img.setAttribute('data-aspect-ratio', node.attrs.aspectRatio);
  }

  // Apply dimensions
  const width = node.attrs.width;
  const height = node.attrs.height;
  if (width) {
    img.style.width = `${width}px`;
    wrapper.style.width = `${width}px`;
  }
  if (height) {
    img.style.height = `${height}px`;
    wrapper.style.height = `${height}px`;
  }

  // Set container alignment based on image alignment attribute
  const justifyContent = alignmentToJustifyContent[alignment] || 'center';
  container.style.display = 'flex';
  container.style.justifyContent = justifyContent;
  container.style.alignItems = 'flex-start';

  // Assemble the DOM
  wrapper.appendChild(img);
  wrapper.appendChild(placeholder);
  figure.appendChild(wrapper);
  figure.appendChild(captionEl);
  container.appendChild(figure);

  // Track resize state
  let isResizing = false;
  let startX = 0;
  let startY = 0;
  let startWidth = 0;
  let startHeight = 0;
  let aspectRatio = 1;

  // Create resize handles
  const handlePositions = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
  const handles: HTMLDivElement[] = [];

  for (const position of handlePositions) {
    const handle = document.createElement('div');
    handle.dataset.resizeHandle = position;
    handle.style.cssText = `
      position: absolute;
      width: 12px;
      height: 12px;
      background: #3b82f6;
      border: 2px solid white;
      border-radius: 50%;
      opacity: 0;
      transition: opacity 0.2s;
      z-index: 10;
      cursor: nwse-resize;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    `;

    // Position the handle
    if (position.includes('top')) handle.style.top = '-6px';
    if (position.includes('bottom')) handle.style.bottom = '-6px';
    if (position.includes('left')) handle.style.left = '-6px';
    if (position.includes('right')) handle.style.right = '-6px';

    // Set cursor based on position
    if (position === 'top-left' || position === 'bottom-right') {
      handle.style.cursor = 'nwse-resize';
    } else {
      handle.style.cursor = 'nesw-resize';
    }

    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();

      isResizing = true;
      startX = e.clientX;
      startY = e.clientY;
      startWidth = img.offsetWidth || width || 200;
      startHeight = img.offsetHeight || height || 200;
      aspectRatio = startWidth / startHeight;

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizing) return;

        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        let newWidth = startWidth;
        let newHeight = startHeight;

        // Calculate new dimensions based on handle position
        if (position.includes('right')) {
          newWidth = startWidth + deltaX;
        } else if (position.includes('left')) {
          newWidth = startWidth - deltaX;
        }

        // Preserve aspect ratio
        newHeight = newWidth / aspectRatio;

        // Apply constraints
        newWidth = Math.max(minWidth, newWidth);
        newHeight = Math.max(minHeight, newHeight);

        // Update visual dimensions
        img.style.width = `${Math.round(newWidth)}px`;
        img.style.height = `${Math.round(newHeight)}px`;
        wrapper.style.width = `${Math.round(newWidth)}px`;
        wrapper.style.height = `${Math.round(newHeight)}px`;
      };

      const onMouseUp = () => {
        if (!isResizing) return;
        isResizing = false;

        // Commit the new dimensions to the document
        const pos = getPos();
        if (pos !== undefined && editor.isEditable) {
          const finalWidth = Math.round(img.offsetWidth);
          const finalHeight = Math.round(img.offsetHeight);

          editor
            .chain()
            .setNodeSelection(pos)
            .updateAttributes('image', {
              width: finalWidth,
              height: finalHeight,
            })
            .run();
        }

        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });

    handles.push(handle);
    wrapper.appendChild(handle);
  }

  // Show handles on hover
  wrapper.addEventListener('mouseenter', () => {
    if (editor.isEditable) {
      for (const h of handles) h.style.opacity = '1';
    }
  });
  wrapper.addEventListener('mouseleave', () => {
    if (!isResizing) {
      for (const h of handles) h.style.opacity = '0';
    }
  });

  // Show placeholder immediately if no src; otherwise hide until loaded
  if (!node.attrs.src) {
    showPlaceholder();
  } else {
    container.style.visibility = 'hidden';
    img.onload = showImage;
  }

  return {
    dom: container,
    update: (updatedNode: ProseMirrorNode) => {
      if (updatedNode.type.name !== 'image') {
        return false;
      }

      // Update src if changed — swap between placeholder and image as needed
      if (updatedNode.attrs.src !== img.src) {
        img.src = updatedNode.attrs.src || '';
        if (!updatedNode.attrs.src) {
          showPlaceholder();
        } else {
          container.style.visibility = 'hidden';
          img.onload = showImage;
        }
      }

      // Update alt if changed
      if (updatedNode.attrs.alt !== img.alt) {
        img.alt = updatedNode.attrs.alt || '';
      }

      // Update title if changed
      if (updatedNode.attrs.title !== img.title) {
        img.title = updatedNode.attrs.title || '';
      }

      // Update caption if changed — appears/updates/hides live
      applyCaption(updatedNode.attrs.caption);

      // Update alignment
      const newAlignment = updatedNode.attrs.alignment || 'center';
      img.className = `align-${newAlignment}`;
      container.style.justifyContent = alignmentToJustifyContent[newAlignment] || 'center';

      // Update dimensions
      const newWidth = updatedNode.attrs.width;
      const newHeight = updatedNode.attrs.height;
      if (newWidth) {
        img.style.width = `${newWidth}px`;
        wrapper.style.width = `${newWidth}px`;
      } else {
        img.style.width = '';
        wrapper.style.width = '';
      }
      if (newHeight) {
        img.style.height = `${newHeight}px`;
        wrapper.style.height = `${newHeight}px`;
      } else {
        img.style.height = '';
        wrapper.style.height = '';
      }

      return true;
    },
    destroy: () => {
      // Cleanup if needed
    },
  };
}

export const ImageExtension = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      blockId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-block-id'),
        renderHTML: (attributes) => {
          return attributes.blockId ? { 'data-block-id': attributes.blockId } : {};
        },
      },
      // Marks the article's designated hero image. Exclusive by convention —
      // the rail's set-hero handler clears it from every other image node.
      // Drives the Hero card preview and the Generate AI replacement target.
      isHero: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-hero') === 'true',
        renderHTML: (attributes) => {
          return attributes.isHero ? { 'data-hero': 'true' } : {};
        },
      },
      // Marks an intentionally decorative image (alt="" is correct, not
      // missing). Editor-side metadata only — undeclared attrs are stripped
      // by the schema on serialize, so this MUST be declared to persist.
      decorative: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-decorative') === 'true',
        renderHTML: (attributes) => {
          return attributes.decorative ? { 'data-decorative': 'true' } : {};
        },
      },
      alignment: {
        default: 'center', // Images are always centered by default
        parseHTML: (element) => {
          if (element.getAttribute('data-alignment')) return element.getAttribute('data-alignment');
          if (element.classList.contains('align-left')) return 'left';
          if (element.classList.contains('align-right')) return 'right';
          return 'center';
        },
        renderHTML: (attributes) => {
          const alignment = attributes.alignment || 'center';
          return { class: `align-${alignment}` };
        },
      },
      width: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('width') || element.style.width;
          return raw ? Number.parseInt(raw, 10) : null;
        },
        renderHTML: (attributes) => {
          return attributes.width ? { width: attributes.width, style: `width: ${attributes.width}px` } : {};
        },
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('height') || element.style.height;
          return raw ? Number.parseInt(raw, 10) : null;
        },
        renderHTML: (attributes) => {
          return attributes.height ? { height: attributes.height, style: `height: ${attributes.height}px` } : {};
        },
      },
      aspectRatio: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-aspect-ratio'),
        renderHTML: (attributes) => {
          return attributes.aspectRatio ? { 'data-aspect-ratio': attributes.aspectRatio } : {};
        },
      },
      isAIGenerated: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-ai-generated') === 'true',
        renderHTML: (attributes) => {
          return attributes.isAIGenerated ? { 'data-ai-generated': 'true' } : {};
        },
      },
      caption: {
        default: null,
        renderHTML: () => {
          return {};
        },
      },
      isStockPhoto: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-stock-photo') === 'true',
        renderHTML: (attributes) => {
          return attributes.isStockPhoto ? { 'data-stock-photo': 'true' } : {};
        },
      },
      source: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-source'),
        renderHTML: () => {
          return {};
        },
      },
      attributionSource: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-attribution-source'),
        renderHTML: () => {
          return {};
        },
      },
      attributionText: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-attribution-text'),
        renderHTML: () => {
          return {};
        },
      },
      photographerName: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-photographer-name'),
        renderHTML: () => {
          return {};
        },
      },
      photographerUrl: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-photographer-url'),
        renderHTML: () => {
          return {};
        },
      },
      sourcePageUrl: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-source-page-url'),
        renderHTML: () => {
          return {};
        },
      },
    };
  },

  /**
   * Custom NodeView that handles alignment correctly.
   * This overrides TipTap's built-in ResizableNodeView which hardcodes
   * justify-content: flex-start on the container.
   */
  addNodeView() {
    // Only use custom NodeView if resize is enabled and we're in an editable context
    if (
      !this.options.resize ||
      !this.options.resize.enabled ||
      typeof document === 'undefined' ||
      !this.editor.isEditable
    ) {
      return null;
    }

    const { directions, minWidth, minHeight } = this.options.resize;

    return (props: NodeViewRendererProps) => {
      return createImageNodeView(props, { minWidth, minHeight, directions });
    };
  },
});

export default ImageExtension;
